import React, { ReactElement, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Redirect, Route, Switch, useLocation } from "react-router";
import { setSidebarMode, VisageSidebarMode } from "src/sidebar";
import { SpintrTypes } from "src/typings";
import { QualityHubListsView } from "../QualityHubListsView";
import { QualityHubDayView } from "../QualityHubDayView";
import { QualityHubTaskListView } from "../QualityHubTaskListView";
import { OperationalTargetsListView } from "../OperationalTargetsListView";
import {
    QualityHubRootViewStateProps as StateProps
} from "./QualityHubRootView.types";
import { SidebarMenu, SidebarMenuGroup, OperationalTargetsCarousel } from "src/components";
import { localize } from "src/l10n";
import { StandardErrorBoundary } from "src/ui";
import { selectAppEnabled, selectInstanceConfig } from "src/redux";
import { Conditional } from "src/components/Conditional";

const basePath = "/quality-hub" as const;

/**
 * Extracts the state properties needed for the Quality Hub root view from the
 * Redux application state.
 *
 * @param {Spintr.AppState} appState - The Redux application state.
 * @return {StateProps} The extracted state properties.
 */
function getStateProps(appState: Spintr.AppState): StateProps {
    const isAdministrator = appState.profile.active.roles.includes("administrators");

    return {
        functionAccountsEnabled: selectInstanceConfig<boolean>(
            appState,
            "enableFunctionAccounts",
        ),
        isAdministrator,
        isHubAdministrator: isAdministrator || (appState.privileges.data || [])
            .some((p: any) => p.value && p.privilegeId === SpintrTypes.Privilege.IsQualityHubAdministrator),
        isEnabled: selectAppEnabled(appState, SpintrTypes.SpintrApp.QualityHub),
        isLeader: (
            appState.profile.active.leadsDepartment ||
            appState.profile.active.leadsOffice ||
            appState.profile.active.leadsCompany
        ),
    };
}

/**
 * The root view for the Quality Hub.
 * 
 * @returns {ReactElement} An element representing the view.
 */
function QualityHubRootView(): ReactElement {
    const location = useLocation();
    const dispatch = useDispatch();
    const {
        isAdministrator,
        isEnabled,
        isHubAdministrator,
        isLeader,
    } = useSelector(getStateProps);

    const l10n = useMemo(() => ({
        hubName: localize("appQualityHub"),
    }), []);

    const sidebarMenuGroups = useMemo<SidebarMenuGroup[]>(
        function _qhRootMemoizeMenuGroups() {
            const currentPath = location.pathname;

            const groups: SidebarMenuGroup[] = [
                { key: "main"
                , label: localize("Meny")
                , items: [
                    { href: `${basePath}`
                    , icon: "grid-6"
                    , key: "day-overview"
                    , label: localize("QH_OUR_DAY")
                    },

                    ...(isHubAdministrator ? [
                    { href: `${basePath}/list`
                    , icon: "arrow-2"
                    , key: "lists-overview"
                    , label: localize("QH_LISTS")
                    }] : []),
                    /*
                    { href: `${basePath}/deviations`
                    , icon: "information"
                    , key: "deviations"
                    , label: localize("Avvikelser")
                    },
                    */
                    ...(isLeader ? [
                    { href: `${basePath}/operational-targets`
                    , icon: "flag"
                    , key: "operational-targets"
                    , label: localize("OPS_TARGETS")
                    }] : []),
                ] },
                ...(isAdministrator ? [
                { key: "misc"
                , label: localize("Ovrigt")
                , items : [
                    { href: `/admin/quality-hub`
                    , icon: "setting"
                    , key: "settings"
                    , label: localize("Installningar")
                    }
                ] }, ] : []),
            ];

            return groups
                .map((group) => ({
                    ...group,
                    items: group.items.map((item) => ({
                        ...item,
                        active: item.active || (
                            currentPath !== basePath
                                ? item.href?.indexOf(currentPath) === 0
                                : item.href === currentPath
                        )
                    })),
                }))
                .filter((group) => group.items.length > 0);
        }, [location.pathname, isAdministrator, isHubAdministrator, isLeader]);

    useEffect(function _QualityHubRootViewMount() {
        dispatch(setSidebarMode(VisageSidebarMode.noMenu));

        return function _QualityHubRootViewUnmount() {
            dispatch(setSidebarMode(VisageSidebarMode.menu));
        };
    }, []);

    const displaySidebar = 1 < sidebarMenuGroups.reduce(
        (acc, group) => acc + group.items.length, 0);

    if (!isEnabled) {
        return <Redirect to="/404" />;
    }

    return (
        <div id="QualityHubRootView">
            <OperationalTargetsCarousel />
            <div className="QualityHubRootView-wrapper">
                <Conditional condition={displaySidebar}>
                    <div className="QualityHubRootView-sidebar">
                        <SidebarMenu collapsible={true}
                                     groups={sidebarMenuGroups}
                                     title={l10n.hubName}/>
                    </div>
                </Conditional>
                <div className="QualityHubRootView-content">
                    <StandardErrorBoundary>
                        <Switch>
                            <Route component={OperationalTargetsListView}
                                   exact={true}
                                   path={`${basePath}/operational-targets`} />

                            <Route component={QualityHubListsView}
                                   exact={false}
                                   path={`${basePath}/list`} />

                            <Route component={QualityHubTaskListView}
                                   exact={true}
                                   path={`${basePath}/:listId/tasks`} />

                            <Route component={QualityHubDayView}
                                   exact={true}
                                   path={`${basePath}/:listId/customize`} />

                            <Route component={QualityHubDayView}
                                   exact={true}
                                   path={`${basePath}/:listId`} />

                            <Route component={QualityHubDayView}
                                   exact={false}
                                   path={`${basePath}`} />
                        </Switch>
                    </StandardErrorBoundary>
                </div>
            </div>
        </div>
    );
}

export default QualityHubRootView;
